import React, { useState, useContext, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Form, Row, Col, message } from 'antd'
import { useNavigate } from 'react-router-dom'
import ActiveTicketTypeSelect from 'components/ActiveTicketTypeSelect'
import ManageAssigneeItem from 'components/ManageAssigneeItem'
import EnumSelect from 'components/EnumSelect'
import MemberSelect from 'components/MemberSelect'
import MemberStatus from 'components/MemberStatus'
import ChatSelect from 'components/Selects/ChatSelect'
import { TicketTypesContext } from 'context/TicketTypes/TicketTypesContext'
import { interactedWithCommonOptions } from 'constants/constants'
import {
  disallowedModalitiesSet,
  commonOptions
} from 'constants/interactionModalities'
import { Label } from 'pages/Ticket/styled'
import { createNewTicket } from 'pages/Ticket/helpers'
import { Button, Wysiwyg, Modal, Input, RadioGroup } from 'ui/styled'
import { PROHIBITED_TICKET_TYPES } from 'constants/subjectTypeConstants'
import { showLeaveModal } from 'components/CreateTicketModal/helpers'
import useTicketCreate from 'apollo/mutations/Tickets/TICKET_CREATE'
import BulkMemberUpload from 'components/BulkCsvUpload'
import useTicketBulkCreate from 'apollo/mutations/Tickets/TICKET_BULK_CREATE'
import useTicketHandlingAttachments from 'utils/hooks/useTicketHandlingAttachments'
import PhoneInput from 'components/PhoneInput'
import { pick } from 'ramda'
import { usOptionalNumberValidator } from 'utils/helpers/usNumberValidator'
import useUserStore from 'stores/user'
import { Can, ACTIONS, SUBJECTS } from 'context/Permissions'

import EnumRadio from 'components/EnumRadio'
import SurveyCreationSection from './SurveyCreationSection/SurveyCreationSection'
import { buildSurveyAnswersPayload } from './SurveyCreationSection/helpers'
import BenefitInquiryFields from './RequestData/BenefitInquiryFields'
import BillingAdvocacyFields from './RequestData/BillingAdvocacyFields'
import DrugListUpdateFields from './RequestData/DrugListUpdateFields'
import FindCareFields from './RequestData/FindCareFields'
import AppointmentFields from './RequestData/AppointmentFields'
import AnnualPhysicalFields from './RequestData/AnnualPhysicalFields'
import SeldonBeattieFields from './RequestData/SeldonBeattieFields'
import BenefitsQuestionFields from './RequestData/BenefitsQuestionFields'
import MedicalNavigationFields from './RequestData/MedicalNavigationFields'
import FinancialProgramFields from './RequestData/FinancialProgramFields'
import PbmContactCenterFields from './RequestData/PbmContactCenterFields'
import PriorAuthorizationStatusFields from './RequestData/PriorAuthorizationStatusFields'
import AccumulatorIssueFields from './RequestData/AccumulatorIssueFields'
import VendorSetupFields from './RequestData/VendorSetupFields/VendorSetupFields'
import ImpactReportFields from './RequestData/ImpactReportFields/ImpactReportFields'
import AccumulatorChangeFields from './RequestData/PlanChangeHandling/AccumulatorChangeFields'
import FertilityMaximumFields from './RequestData/PlanChangeHandling/FertilityMaximumFields'
import GroupNumberChangeFields from './RequestData/PlanChangeHandling/GroupNumberChangeFields'
import PlanChangeHandlingFields from './RequestData/PlanChangeHandling/PlanChangeHandlingFields'
import NewImplementationFields from './RequestData/NewImplementationFields/NewImplementationFields'
import GroupTermingFields from './RequestData/GroupTermingFields'
import ClaimFileProcessingFields from './RequestData/ClaimFileProcessingFields/ClaimFileProcessingFields'
import EligibilityInquiryFields from './RequestData/EligibilityInquiryFields/EligibilityInquiryFields'

import { RightSide, RadioWrapper } from './styled'

const FormItem = Form.Item

const directionOptions = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' }
]

/**
 * @param {{ memberId?: string, setVisible: (visible: boolean) => void, chatId?: string }} props
 */
const CreateTicketModal = ({
  memberId: memberIdProp,
  setVisible = () => {},
  chatId = undefined
}) => {
  const userId = useUserStore(s => s.userId)
  const [form] = Form.useForm()
  const { setFieldsValue, setFieldValue } = form
  const navigate = useNavigate()
  const [memberId, setMemberId] = useState(memberIdProp)

  const [specificAttributes, setSpecificAttributes] = useState([])
  const haveBulkAttributes = specificAttributes?.length > 0

  const { ticketTypes, ticketTypesLoading } = useContext(TicketTypesContext)
  const [notes, setNotes] = useState(false)
  const [ticketType, setTicketType] = useState({})
  const [selectedEntity, setSelectedEntity] = useState({})
  const [timeSlots, setTimeSlots] = useState([])
  const [isPersonSelected, setPersonSelected] = useState(true)
  const [showInteractionInfo, setShowInteractionInfo] = useState(false)
  const [isEmailInteraction, setIsEmailInteraction] = useState(false)
  const [surveyId, setSurveyId] = useState(null)
  const [surveyAnswersMap, setSurveyAnswersMap] = useState({})

  const { onTicketBulkCreate } = useTicketBulkCreate()
  const { onTicketCreate, loading } = useTicketCreate()
  const { uploadAttachments } = useTicketHandlingAttachments()

  const onModalClose = () => {
    form.resetFields()
    setVisible(false)
  }
  const {
    isAppointment,
    isBilling,
    isFindCare,
    isMedical,
    isAnnualPhysical,
    isDrugSearch,
    isSeldon,
    isBenefitsQuestion,
    isFinancialProgram,
    isSpecialtyCostPlusConversion,
    isPbmContactCenter,
    isOverride,
    isPriorAuthorization,
    isBenefitInquiry,
    isDrugListUpdate,
    isPriorAuthorizationStatus,
    isAccumulatorIssue,
    isVendorSetup,
    isImpactReport,
    isAccumulatorChange,
    isFertilityMaximum,
    isGroupNumberChange,
    isPlanChangeHandling,
    isNewImplementation,
    isGroupTerming,
    isClaimFileProcessing,
    isEligibilityInquiry
  } = ticketType

  const create = async () => {
    form
      .validateFields()
      .then(async values => {
        // if person radio is selected, we assign a ticket to a HG
        const assignee = values.assigned_to_me ? userId : values.assigned_to_id
        // but if person radio is selected, and we don't have an assignee,
        // we assign it to first available navigator
        const assigned_to_id = isPersonSelected && !assignee ? null : assignee

        const { variables, ticketAttributes } = createNewTicket({
          values,
          memberId,
          assigned_to_id,
          notes,
          userId,
          selectedEntity,
          timeSlots,
          ticketType
        })

        const surveyAttributes = surveyId
          ? {
              survey_id: surveyId,
              survey_answers_attributes:
                buildSurveyAnswersPayload(surveyAnswersMap)
            }
          : null

        if (variables) {
          if (haveBulkAttributes) {
            const source_attributes = isFinancialProgram
              ? variables?.source_attributes?.financial_data_value_related
              : {}

            const interaction_attributes = pick(
              [
                'direction',
                'interacted_with',
                'interaction_modality',
                'interaction_target_id',
                'navigator_id',
                'notes',
                'user_id',
                'contact_name',
                'contact_detail'
              ],
              variables
            )

            const payload = {
              attributes: {
                ...ticketAttributes,
                source_attributes,
                interaction_attributes,
                specific_ticket_attributes: specificAttributes,
                ...(surveyAttributes && surveyAttributes)
              }
            }

            delete payload.attributes.member_id

            const { isErrorFree } = await onTicketBulkCreate(payload)
            if (isErrorFree) {
              onModalClose()
            }
          } else {
            const { r } = await onTicketCreate({
              attributes: {
                ...ticketAttributes,
                initial_interaction_attributes: variables,
                ...(surveyAttributes && surveyAttributes)
              }
            })

            const ticketId = r?.data?.ticketCreate?.id
            const isSpecialErrorMessage = !!r?.data?.errors?.[0]?.message

            await uploadAttachments({
              ticketType,
              sourceId: r?.data?.ticketCreate?.resource?.id,
              attachments: values?.attachments
            })

            if (ticketId) {
              navigate(`/ticket/${ticketId}`)
              onModalClose()
            } else if (!isSpecialErrorMessage) {
              message.error('Something went wrong: Failed to create ticket.')
            }
          }
        }
      })
      .catch(() => {
        // AntD Form.validateFields rejects on validation failure — expected, no action needed
      })
  }

  const shouldConfirmNavigation = () => {
    const { getFieldsValue } = form

    const isFormFilled = Object.values(getFieldsValue()).some(value => !!value)
    const hasSurveyAnswers = Object.keys(surveyAnswersMap).length > 0

    if (!isFormFilled && !hasSurveyAnswers) {
      onModalClose()
    } else {
      showLeaveModal({ onModalClose })
    }
  }

  if (!isPersonSelected) {
    form.setFields([{ name: 'assigned_to', errors: [] }])
  }

  const validateAssignedFields = async () => {
    const assignToId = await form.getFieldValue('assigned_to_id')
    const assignToMe = await form.getFieldValue('assigned_to_me')
    const assignmentNotes = await form.getFieldValue('assignment_notes')

    if (!assignToId && !assignToMe) {
      return Promise.reject(new Error('Please assign a health guide'))
    }

    if (!assignmentNotes && !assignToMe) {
      return Promise.reject(new Error('Please add assignment notes'))
    }

    return Promise.resolve()
  }

  const onSurveyIdChange = useCallback(id => {
    setSurveyId(id)
    setSurveyAnswersMap({})
  }, [])

  const onSurveyAnswerChange = useCallback((questionId, answers) => {
    setSurveyAnswersMap(prev => ({ ...prev, [questionId]: answers }))
  }, [])

  const onValuesChange = value => {
    const { assigned_to_id, assigned_to_me, interaction_modality } = value

    if (interaction_modality) {
      const showDetails = ['phonecall', 'fax', 'email'].includes(
        interaction_modality
      )
      setShowInteractionInfo(showDetails)
      if (showDetails) setIsEmailInteraction(interaction_modality === 'email')
    }

    if (assigned_to_id || assigned_to_me)
      form.setFields([{ name: 'assigned_to', errors: [] }])

    if (assigned_to_id) {
      form.setFieldValue('assigned_to_me', undefined)
    }

    if (assigned_to_me) {
      form.setFieldValue('assigned_to_id', undefined)
    }
  }

  return (
    <Modal
      title="Create a New Ticket"
      width="1200px"
      open
      onCancel={shouldConfirmNavigation}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onModalClose}>Cancel</Button>
          <Button
            btntype="primary"
            margin="0 20px 0 0"
            onClick={create}
            loading={loading}
            data-test="reassignTicketBtn"
          >
            Create
          </Button>
        </div>
      }
    >
      <RightSide>
        <Form form={form} autoComplete="off" onValuesChange={onValuesChange}>
          <Label required>Member</Label>
          {!haveBulkAttributes && (
            <>
              <FormItem
                name="member_id"
                rules={[
                  {
                    required: true,
                    message: 'Please enter member'
                  }
                ]}
                initialValue={memberId}
              >
                <MemberSelect
                  variables={{ only_registered: !!isBilling }}
                  onChange={v => setMemberId(v)}
                />
              </FormItem>
              {memberId && <MemberStatus memberId={memberId} />}

              <Label>Attach to chat</Label>
              <FormItem name="chat_id" initialValue={chatId}>
                <ChatSelect
                  memberId={memberId}
                  data-test="createTicketFormChatIdSelect"
                />
              </FormItem>
            </>
          )}

          <Can I={ACTIONS.CREATE} a={SUBJECTS.BULK_TICKETS}>
            <BulkMemberUpload
              disabled={memberId}
              submitBulkCreate={setSpecificAttributes}
              type="ticket_member"
            />
          </Can>
          <Label required>Subject</Label>
          <FormItem
            name="ticket_type_id"
            rules={[
              {
                required: true,
                message: 'Please enter ticket type'
              }
            ]}
          >
            <ActiveTicketTypeSelect
              showAny={false}
              onChange={id => {
                const type = ticketTypes.find(i => i.id === id)
                setTicketType(type)
                setSurveyId(type?.survey_id)
                setSurveyAnswersMap({})
              }}
              disabled={ticketTypesLoading}
              disabledOptions={PROHIBITED_TICKET_TYPES}
              isBulkCreateOnly={haveBulkAttributes}
            />
          </FormItem>

          {isPbmContactCenter && (
            <PbmContactCenterFields
              isOverride={isOverride}
              isPriorAuthorization={isPriorAuthorization}
            />
          )}

          {isAccumulatorIssue && <AccumulatorIssueFields form={form} />}

          {isBenefitInquiry && <BenefitInquiryFields form={form} />}

          {isPriorAuthorizationStatus && <PriorAuthorizationStatusFields />}

          {isBilling && <BillingAdvocacyFields form={form} />}

          {isDrugListUpdate && <DrugListUpdateFields form={form} />}

          {isDrugSearch && (
            <>
              <Label required>Category</Label>
              <FormItem
                name="categories"
                rules={[
                  { required: true, message: 'Please specify categories' }
                ]}
              >
                <EnumSelect enum_name="Categories" />
              </FormItem>
            </>
          )}

          {isMedical && <MedicalNavigationFields />}

          {isFindCare && <FindCareFields />}

          {isAppointment && (
            <AppointmentFields
              memberId={memberId}
              timeSlots={timeSlots}
              onSaveEntity={setSelectedEntity}
              onSaveTimeSlots={setTimeSlots}
              setFieldsValue={setFieldsValue}
              isForm
            />
          )}

          {isAnnualPhysical && <AnnualPhysicalFields form={form} />}

          {isSeldon && <SeldonBeattieFields form={form} />}

          {isBenefitsQuestion && <BenefitsQuestionFields form={form} />}

          {(isFinancialProgram || isSpecialtyCostPlusConversion) && (
            <FinancialProgramFields form={form} />
          )}

          {isVendorSetup && <VendorSetupFields form={form} />}

          {isNewImplementation && <NewImplementationFields form={form} />}

          {isGroupTerming && <GroupTermingFields form={form} />}

          {isClaimFileProcessing && <ClaimFileProcessingFields form={form} />}

          {isImpactReport && <ImpactReportFields form={form} />}

          {isAccumulatorChange && <AccumulatorChangeFields form={form} />}

          {isFertilityMaximum && <FertilityMaximumFields form={form} />}

          {isGroupNumberChange && <GroupNumberChangeFields form={form} />}

          {isPlanChangeHandling && <PlanChangeHandlingFields form={form} />}

          {isEligibilityInquiry && <EligibilityInquiryFields form={form} />}

          {ticketType?.enable_surveys && (
            <SurveyCreationSection
              surveyId={surveyId}
              answersMap={surveyAnswersMap}
              onSurveyIdChange={onSurveyIdChange}
              onAnswerChange={onSurveyAnswerChange}
            />
          )}

          <Row style={{ paddingTop: 10 }}>
            <Col span={8}>
              <RadioWrapper>
                <Label required>Source</Label>
                <FormItem
                  name="interaction_modality"
                  rules={[
                    {
                      required: true,
                      message: 'Please select an interaction type'
                    }
                  ]}
                >
                  <EnumRadio
                    hideValues={disallowedModalitiesSet}
                    data-test="createTicketFormSourceRadio"
                    query="INTERACTION_MODALITY_CODE"
                    commonOptions={commonOptions}
                  />
                </FormItem>
              </RadioWrapper>
            </Col>
            <Col span={8} style={{ paddingLeft: 20, paddingRight: 20 }}>
              <RadioWrapper>
                <Label required>Direction</Label>
                <FormItem
                  name="direction"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a direction'
                    }
                  ]}
                >
                  <RadioGroup
                    name="direction"
                    placeholder="Direction of Interaction"
                    data-test="createTicketFormDirectionRadio"
                    options={directionOptions}
                    style={{ flexDirection: 'column' }}
                  />
                </FormItem>
              </RadioWrapper>
            </Col>
            <Col span={8}>
              <RadioWrapper>
                <Label required>Party Interacted With</Label>
                <FormItem
                  name="interacted_with"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a party interacted with'
                    }
                  ]}
                >
                  <EnumRadio
                    query="InteractionInteractedWith"
                    commonOptions={interactedWithCommonOptions}
                  />
                </FormItem>
              </RadioWrapper>
            </Col>
          </Row>

          {showInteractionInfo && (
            <Row>
              <Col span={8}>
                <Label>Contact Name</Label>
                <FormItem name="contact_name">
                  <Input
                    placeholder="Contact Name"
                    disabled={haveBulkAttributes}
                    data-test="createTicketFormContactNameInput"
                  />
                </FormItem>
              </Col>
              <Col span={8} style={{ paddingLeft: 10, paddingRight: 10 }}>
                <Label>Contact Detail</Label>
                {isEmailInteraction ? (
                  <FormItem
                    name="contact_detail"
                    rules={[
                      {
                        type: 'email',
                        message: 'The input is not valid e-mail'
                      }
                    ]}
                  >
                    <Input placeholder="E-mail" disabled={haveBulkAttributes} />
                  </FormItem>
                ) : (
                  <FormItem
                    name="contact_detail"
                    rules={[
                      () => ({
                        validator(_, value) {
                          return value
                            ? usOptionalNumberValidator(value)
                            : Promise.resolve()
                        }
                      })
                    ]}
                  >
                    <PhoneInput disabled={haveBulkAttributes} />
                  </FormItem>
                )}
              </Col>
            </Row>
          )}

          <Label>Ticket Notes</Label>
          <Wysiwyg
            value={notes}
            onChange={e => setNotes(e)}
            data-test="createNewTicketPageTicketNotesArea"
            placeholder="Notes to add to the ticket"
          />

          <Label required>Assigned To</Label>
          <FormItem
            name="assigned_to"
            rules={[
              {
                validator: isPersonSelected && validateAssignedFields
              }
            ]}
          >
            <ManageAssigneeItem
              setFieldValue={setFieldValue}
              isPersonSelected={isPersonSelected}
              setPersonSelected={setPersonSelected}
              isTicketCreation
            />
          </FormItem>
        </Form>
      </RightSide>
    </Modal>
  )
}

CreateTicketModal.propTypes = {
  memberId: PropTypes.string.isRequired,
  setVisible: PropTypes.func,
  chatId: PropTypes.string
}

export default CreateTicketModal
